import Button from '../../../../components/Button';
import GiftCard from '../../../../components/GiftCard';
import {
  useGiftForm,
} from '../../../../components/GiftFormContext';
import { useDispatch, useSelector } from 'react-redux';
import { selectTokenByNetworkAndAddress } from '../../../../redux/slices/tokenSlice';
import TextInput from '../../../../components/Inputs/Text';
import { hapticsImpactLight } from '../../../../common/haptic';
import DeliveryMethods from './components/DeliveryMethods';
import { Controller } from 'react-hook-form';
import { useCreateCustomGiftCardDesignMutation } from '../../../../redux/api/authorized/customGiftCardDesigns';
import { compressBase64Image } from '../../../../common/images';
import { openModal, closeModal } from '../../../../redux/slices/modals';
import { Capacitor } from '@capacitor/core';
import {
  PHONE_MEDIA_QUERY,
  useMediaQuery,
} from '../../../../common/responsive';
import { selectPrivyToken } from '../../../../redux/slices/auth';
import {
  EVENT_GIFT_FLOW_CUSTOM_IMAGE_CREATED,
  EVENT_GIFT_FLOW_CUSTOMIZATION_COMPLETED,
  EVENT_GIFT_FLOW_MESSAGE_CHANGED,
  sendEvent,
} from '../../../../common/analytics';
import styled, { keyframes } from 'styled-components';
import StepWrapper from '../../../../components/StepWrapper';
import { StaggerContainer, StaggerItem } from '../../../../components/Stagger';
import GiftFlowButtonContainer from '../../ButtonContainer';
import { useFlag } from '@unleash/proxy-client-react';

const Customize = ({
  onContinue,
  buttonLabel = 'Select Amount',
  isModal = false,
  currentStep = 0,
  isBack = false,
}) => {
  const {
    usdAmount,
    image,
    setValue,
    trigger,
    targetTokenAddress,
    network,
    tokenAmount,
    delivery,
    control,
    errors,
    message,
    senderName,
    showFreeGift,
  } = useGiftForm();

  const targetToken = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, network, targetTokenAddress),
  );

  const dispatch = useDispatch();
  const [createCustomGiftCardDesign] = useCreateCustomGiftCardDesignMutation();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const isNative = Capacitor.isNativePlatform();
  const privyToken = useSelector(selectPrivyToken);
  const freeGiftAllInputs = useFlag('freeGiftFullInputs');

  const handleDeliveryMethodChange = (method) => {
    hapticsImpactLight();
    setValue('delivery', method);
  };

  const handleImageComplete = async (croppedDataUrl) => {
    hapticsImpactLight();
    const compressedImage = await compressBase64Image(croppedDataUrl);

    if (privyToken) {
      // User is logged in: save to backend and use returned URL
      const response = await createCustomGiftCardDesign({
        image: compressedImage,
        type: delivery === 'mail' ? 'physical' : 'digital',
      });

      if (response.data?.imageUrl) {
        // Set the image value - this will trigger GiftCard to update
        setValue('image', response.data.imageUrl, { shouldValidate: true });
        await trigger('image'); // Trigger validation to clear any errors
      }
    } else {
      // User is not logged in: use compressed image data URL directly
      setValue('image', compressedImage, { shouldValidate: true });
      await trigger('image'); // Trigger validation to clear any errors
    }

    // Close the crop modal
    dispatch(closeModal());
    sendEvent(EVENT_GIFT_FLOW_CUSTOM_IMAGE_CREATED, {
      is_free_gift: showFreeGift,
    });
  };

  const handleImageCancel = () => {
    dispatch(closeModal());
  };

  const handleImageSelection = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const imageData = reader.result;
      dispatch(
        openModal({
          name: 'cardImageCrop',
          props: {
            image: imageData,
            isPhysical: delivery === 'mail',
            onComplete: handleImageComplete,
            onClose: handleImageCancel,
          },
        }),
      );
    };
    reader.readAsDataURL(file);
  };

  const handleCreateCustomImage = () => {
    hapticsImpactLight();

    // On mobile/native: use native file picker
    if (isMobile || isNative) {
      // Create and trigger hidden file input
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      // Don't set capture attribute - allows user to choose between camera and photo library
      input.onchange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
          handleImageSelection(file);
        }
      };
      input.click();
    } else {
      // On web: open CustomizeCard modal with drag and drop
      dispatch(
        openModal({
          name: 'customizeCard',
          props: {
            isPhysical: delivery === 'mail',
            onComplete: handleImageComplete,
            onClose: handleImageCancel,
          },
        }),
      );
    }
  };

  const isValidForm =
    !errors.email?.message &&
    !errors.message?.message &&
    !errors.image?.message &&
    !errors.delivery?.message;


  return (
    <StepWrapper $back={isBack}>
        <StaggerContainer>
          <StaggerItem $delay={1}>
            <div style={{ width: '100%' }}>
              <GiftCard
                editable={true}
                hideValue
                usdAmount={usdAmount}
                tokenAmount={tokenAmount}
                cardImage={image}
                token={targetToken}
                isFreeGift={showFreeGift}
                onCardImageChange={(value) => setValue('image', value)}
                isPhysical={delivery === 'mail'}
                giftCardText={message}
                senderName={senderName}
                onCreateCustomImage={handleCreateCustomImage}
                isModal={isModal}
                currentStep={currentStep}
              />
            </div>
          </StaggerItem>
          {(!showFreeGift || (showFreeGift && freeGiftAllInputs)) && (
            <StaggerItem $delay={3}>
              <DeliveryMethods
                handleDeliveryMethodChange={handleDeliveryMethodChange}
                delivery={delivery}
              />
            </StaggerItem>
          )}
          <StaggerItem $delay={5}>
            {delivery === 'email' && (
              <Controller
                name='email'
                control={control}
                render={({ field }) => (
                  <TextInput
                    label='Recipient Email'
                    inputMode='email'
                    type='email'
                    placeholder='recipient@example.com'
                    error={errors?.email?.message}
                    {...field}
                  />
                )}
              />
            )}
            {delivery === 'mail' && (
              <Controller
                name='senderName'
                control={control}
                render={({ field }) => (
                  <TextInput
                    label='From'
                    inputMode='text'
                    type='text'
                    placeholder='Sender Name (Optional)'
                    error={errors?.senderName?.message}
                    {...field}
                  />
                )}
              />
            )}
            <Controller
              name='message'
              control={control}
              render={({ field }) => (
                <TextInput
                  label='Personal Message'
                  inputMode='text'
                  type='text'
                  placeholder='Write a personal message'
                  error={errors?.message?.message}
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    sendEvent(EVENT_GIFT_FLOW_MESSAGE_CHANGED, {
                      message: e.target.value,
                      is_free_gift: showFreeGift,
                    });
                  }}
                />
              )}
            />
          </StaggerItem>
          <StaggerItem $delay={7} style={{ marginTop: '20px', width: '100%' }}>
            <Button
              fullWidth
              clickAnimation='bouncy'
              onClick={() => {
                sendEvent(EVENT_GIFT_FLOW_CUSTOMIZATION_COMPLETED, {
                  is_free_gift: showFreeGift,
                });
                onContinue();
              }}
              disabled={!isValidForm}
            >
              {buttonLabel}
            </Button>
          </StaggerItem>
        </StaggerContainer>
      </StepWrapper>
  );
};

export default Customize;
